import Head from "next/head";
import { Header } from "@/components/LandingPage/Header";
import { Footer } from "@/components/LandingPage/Footer";

interface ChangelogEntry {
  date: string; // Format: "YYYY-MM-DD"
  title: string;
  description: string;
  image?: string; // Optional screenshot/image
}

const changelogEntries: ChangelogEntry[] = [
  {
    date: "2025-10-17",
    title: "Small UI Fixes",
    description:
      "Various UI improvements and bug fixes across the platform. Enhanced user experience with better visual consistency and resolved minor interface issues.",
  },
  {
    date: "2025-07-28",
    title: "Quick JSON Storage Feature",
    description:
      "Introduced temporary JSON storage functionality allowing users to quickly store and share JSON data without creating accounts. Features automatic expiration, public accessibility, and seamless integration with existing tools.",
  },
  {
    date: "2025-07-08",
    title: "JSON Developer Tools Suite",
    description:
      "Launched a comprehensive suite of JSON developer tools including JSON Prettifier for readable formatting, JSON Minifier for optimized file sizes, and JSON Diff Checker for comparing objects.",
  },
  {
    date: "2025-05-07",
    title: "API v2 Release",
    description:
      "Released API v2 with significant performance improvements and enhanced body parameter handling. The new version offers faster response times, better error handling, and more flexible request processing for improved developer experience.",
  },
];

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default function Changelog() {
  return (
    <div className="bg-[#0F0F0F] min-h-screen">
      <Head>
        <title>Changelog - myJson | Latest Updates & Features</title>
        <meta
          name="description"
          content="Stay updated with the latest features, improvements, and updates to myJson. Track our development progress and new capabilities."
        />
        <meta
          name="keywords"
          content="changelog, updates, features, myJson, JSON API, development"
        />
        <meta name="author" content="myJson.online" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://myjson.online/changelog" />
        <meta
          property="og:title"
          content="Changelog - myJson | Latest Updates & Features"
        />
        <meta
          property="og:description"
          content="Stay updated with the latest features, improvements, and updates to myJson."
        />
        <meta
          property="og:image"
          content="https://myjson.online/og-image.png"
        />
        <meta property="og:site_name" content="myJson.online" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:url"
          content="https://myjson.online/changelog"
        />
        <meta
          property="twitter:title"
          content="Changelog - myJson | Latest Updates & Features"
        />
        <meta
          property="twitter:description"
          content="Stay updated with the latest features, improvements, and updates to myJson."
        />
        <meta
          property="twitter:image"
          content="https://myjson.online/og-image.png"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://myjson.online/changelog" />
      </Head>

      <Header dark={false} />

      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          {/* Page Header */}
          <div className="mb-16 mt-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Changelog
            </h1>
            <p className="text-xl text-gray-300 mb-16 border-b border-gray-800 pb-8">
              Stay informed about our latest developments
            </p>
          </div>

          {/* Changelog Entries */}
          <div className="space-y-12">
            {changelogEntries.map((entry, index) => (
              <article key={entry.date} className="group">
                <div className="flex flex-col lg:flex-row lg:gap-8">
                  {/* Date Column */}
                  <div className="lg:w-48 flex-shrink-0 mb-4 lg:mb-0">
                    <time className="text-sm font-medium text-gray-400">
                      {formatDate(entry.date)}
                    </time>
                  </div>

                  {/* Content Column */}
                  <div className="flex-1">
                    <div className="space-y-4">
                      <h2 className="text-2xl font-bold text-white">
                        {entry.title}
                      </h2>
                      <div className="prose prose-invert max-w-none">
                        <p className="text-gray-300 leading-relaxed text-lg">
                          {entry.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                {index < changelogEntries.length - 1 && (
                  <div className="mt-12 border-t border-gray-800"></div>
                )}
              </article>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-20 text-center">
            <p className="text-gray-400 text-sm">
              Want to stay updated? Follow us on{" "}
              <a
                href="https://twitter.com/co_demers"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple hover:text-purple/80 transition-colors"
              >
                Twitter
              </a>{" "}
              for real-time updates.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
